/**
 * Набор функций управления одиночкой (singleton), который возвращает
 * {@link makeSingleton}.
 *
 * Все функции — обычные замыкания без привязки к `this`, поэтому их безопасно
 * деструктурировать и ре-экспортировать под прикладными именами.
 *
 * @typeParam T Тип создаваемого экземпляра.
 * @typeParam A Кортеж аргументов фабрики `create`.
 */
export interface Singleton<T, A extends unknown[]> {
  /**
   * Возвращает экземпляр, создавая его при первом обращении.
   *
   * Ленивое создание возможно только для беспараметрной фабрики. Если фабрика
   * объявлена с обязательными аргументами, а экземпляр ещё не создан через
   * {@link Singleton.initInstance} или {@link Singleton.setInstance}, будет
   * выброшена ошибка — это защищает от молчаливого создания с `undefined`.
   *
   * @throws {Error} Если фабрика требует аргументы, но экземпляр ещё не
   * инициализирован.
   */
  getInstance: () => T;

  /**
   * Создаёт и кэширует экземпляр, передавая в фабрику явные аргументы.
   * Предназначена для инициализации на этапе bootstrap приложения.
   *
   * @param args Аргументы фабрики `create`.
   * @returns Созданный экземпляр.
   */
  initInstance: (...args: A) => T;

  /**
   * Подменяет текущий экземпляр готовым значением. Удобно для подстановки
   * тестового дублёра.
   *
   * @param value Экземпляр, который вернут последующие вызовы
   * {@link Singleton.getInstance}.
   */
  setInstance: (value: T) => void;

  /**
   * Сбрасывает закэшированный экземпляр. Следующий вызов
   * {@link Singleton.getInstance} (для беспараметрной фабрики) или
   * {@link Singleton.initInstance} создаст экземпляр заново. Обычно вызывается
   * в `beforeEach` для изоляции тестов.
   */
  resetInstance: () => void;
}

/**
 * Создаёт управляемый одиночка (singleton) с явными точками инициализации и
 * сброса.
 *
 * Утилита снимает две типичные проблемы модульных синглтонов:
 * 1. **Неявные зависимости** — фабрика `create` может принимать параметры,
 *    которые передаются явно через `initInstance` (например, на этапе bootstrap
 *    приложения), а не «добываются» из глобального состояния.
 * 2. **Изоляция в тестах** — `setInstance` позволяет подменить экземпляр
 *    тестовым дублёром, а `resetInstance` — сбросить кэш между тестами, не
 *    прибегая к мокам модулей.
 *
 * Тип экземпляра ограничен `object`, поэтому хранимое значение всегда либо
 * объект, либо `null` — этого достаточно, чтобы отличить «ещё не создан» от
 * «создан» обычной проверкой `!instance`, без отдельного флага.
 *
 * @example Беспараметрная фабрика (ленивое создание при первом обращении)
 * ```ts
 * const { getInstance, setInstance, resetInstance } = makeSingleton(() => createLogger())
 *
 * export const useLogger = getInstance      // () => Logger
 * export const setLogger = setInstance      // для тестов
 * export const resetLogger = resetInstance  // для тестов
 * ```
 *
 * @example Параметризованная фабрика (явная инициализация на bootstrap)
 * ```ts
 * const { getInstance, initInstance, setInstance, resetInstance } =
 *   makeSingleton((deps: AnalyticsDeps) => createAnalytics(deps))
 *
 * export const useAnalytics = getInstance    // () => Analytics — на местах вызова
 * export const initAnalytics = initInstance  // (deps) => Analytics — на bootstrap
 * export const setAnalytics = setInstance
 * export const resetAnalytics = resetInstance
 * ```
 *
 * @typeParam T Тип создаваемого экземпляра.
 * @typeParam A Кортеж аргументов фабрики `create`. По умолчанию `[]`.
 *
 * @param create Фабрика экземпляра. Может быть беспараметрной или принимать
 * аргументы. Вызывается не более одного раза до следующего `resetInstance`.
 *
 * @returns Набор функций управления синглтоном: {@link Singleton}.
 */
export const makeSingleton = <T extends object, A extends unknown[] = []>(
  create: (...args: A) => T,
): Singleton<T, A> => {
  let instance: T | null = null;

  const initInstance = (...args: A): T => {
    instance = create(...args);

    return instance;
  };

  const setInstance = (value: T): void => {
    instance = value;
  };

  const resetInstance = (): void => {
    instance = null;
  };

  const getInstance = (): T => {
    if (instance) {
      return instance;
    }

    if (create.length > 0) {
      throw new Error('makeSingleton: фабрика требует аргументы — вызовите initInstance(...) до getInstance()');
    }

    return initInstance(...([] as unknown as A));
  };

  return { getInstance, initInstance, setInstance, resetInstance };
};
